import { EntityRefUtil, formatDateWithUserTimezone, formatTimeWithUserTimezone, GetRepairsRequestDTO, GetRepairsResponseDTO } from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';

import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';

const columnHelper = createColumnHelper<GetRepairsResponseDTO['data'][number]>();

export const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2">
          <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)} aria-label="Select all" />
        </label>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
        </label>
      </div>
    ),
    meta: {
      cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
    },
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.display({
    id: 'asset',
    header: 'Asset',
    cell: ({ row }) => {
      const value = row.original.asset;
      return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
    },
    meta: { columnGroup: ASSET_SECTION_LABEL },
  }),
  columnHelper.accessor('id', {
    header: 'Record ID',
    enableGrouping: false,
  }),
  columnHelper.accessor((row) => formatDateWithUserTimezone(new Date(row.repairDate), useUserStore.getState().user?.timezone), {
    id: 'createdDate',
    header: 'Created Date',
    enableSorting: true,
    enableGrouping: true,
    enableColumnFilter: true,
    meta: {
      filterType: 'date-range',
    },
  }),
  columnHelper.accessor((row) => formatTimeWithUserTimezone(new Date(row.repairDate), useUserStore.getState().user?.timezone), {
    id: 'createdTime',
    header: 'Created Time',
    enableSorting: true,
    enableGrouping: false,
  }),
  columnHelper.accessor((row) => row.repairedByUser, {
    id: 'repairedBy',
    header: 'Created By',
    cell: ({ row }) => {
      const repair = row.original;
      const repairedBy = repair.repairedByUser;
      const fullName = EntityRefUtil.createUserFullNameRef(repairedBy);
      const value = fullName || '-';
      const avatarBackgroundColorClass = getAvatarBackgroundColorClass(repairedBy?.firstName || '');

      const repairedByFullInitials = initials(fullName);
      return (
        <UserAvatarLabel
          value={value}
          operatedByFullInitials={repairedByFullInitials}
          avatarUrl={repairedBy?.avatarUrl || ''}
          avatarBackgroundColorClass={avatarBackgroundColorClass}
        />
      );
    },
    getGroupingValue: (row) => {
      return EntityRefUtil.createUserFullNameRef(row.repairedByUser, 'None');
    },
    enableGrouping: true,
    enableColumnFilter: true,
    meta: {
      filterType: 'async-multi-select',
      getFilterOptionsFn: async (inputValue, value) => {
        const filters = inputValue ? { filters: { search: inputValue } } : undefined;
        const { data: sitemateUserData } = await (
          await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
        ).json();
        const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();

        const resultsMap = new Map<string, { label: string; value: string }>();
        sitemateUserData.forEach((user) => {
          if (user.firstName || user.lastName) {
            resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
          }
        });
        userData.forEach((user) => {
          if (user.firstName || user.lastName) {
            resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
          }
        });

        if (value?.length) {
          const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
          if (missingIds.length) {
            const { data: missingSitemateUsers } = await (
              await ApiService.GetUsers({
                limit: 100,
                filters: { id: missingIds, userType: 'sitemate-users' },
              })
            ).json();
            const { data: missingUsers } = await (
              await ApiService.GetUsers({
                limit: 100,
                filters: { id: missingIds },
              })
            ).json();

            for (const user of missingSitemateUsers) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
            for (const user of missingUsers) {
              if (user.firstName || user.lastName) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }
        }

        return Array.from(resultsMap.values());
      },
    },
  }),
  columnHelper.accessor((row) => row.form?.name, {
    id: 'formName',
    header: 'Form Name',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
    },
    meta: {
      filterType: 'text',
    },
    enableColumnFilter: true,
    enableGrouping: true,
  }),
  columnHelper.accessor((row) => row.form?.number, {
    id: 'formNumber',
    header: 'Form ID',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? <span>{EntityRefUtil.createFormRef(value)}</span> : <span className="text-secondary-text">-</span>;
    },
    meta: {
      filterType: 'text',
    },
    enableColumnFilter: true,
    enableGrouping: true,
  }),
  columnHelper.accessor('number', {
    header: 'Repair Number',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? <span>{`R${value}`}</span> : <span className="text-secondary-text">-</span>;
    },
    enableGrouping: true,
  }),
] as AccessorKeyColumnDef<GetRepairsResponseDTO['data'][number]>[];

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...columns
    .filter((column) => {
      const availableGroupColumns = ['repairedBy', 'formName', 'formNumber'];
      return !column.accessorKey && column.id ? availableGroupColumns.includes(column.id) : availableGroupColumns.includes(column.accessorKey);
    })
    .map((column) => ({ label: column.header?.toString() || '', accessor: !column.accessorKey && column.id ? column.id : column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<(typeof columns)[number]['id'] | (typeof columns)[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetRepairsRequestDTO['filters']>['fields']>
> = {
  formName: 'formName',
  formNumber: 'formNumber',
  contact: 'contactId',
  createdDate: 'createdDate',
};
